import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { subscribeToSWState, applyUpdate } from '../services/pwa';

interface PWAUpdateBannerProps {
  className?: string;
}

export function PWAUpdateBanner({ className = '' }: PWAUpdateBannerProps) {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToSWState((state) => {
      setIsUpdateAvailable(state.isUpdateAvailable);
      if (state.isUpdateAvailable) {
        setShowBanner(true);
      }
    });

    return unsubscribe;
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await applyUpdate();
    } catch (error) {
      console.error('Failed to apply update:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  if (!isUpdateAvailable || !showBanner) {
    return null;
  }

  return (
    <div className={`fixed top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-auto md:max-w-lg z-50 ${className}`}>
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-white font-medium">Update available</p>
          <p className="text-white/80 text-sm">Refresh to get the latest features</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            disabled={isUpdating}
          >
            <X className="w-5 h-5 text-white/80" />
          </button>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-white/90 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
            {isUpdating ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAUpdateBanner;
